import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Facebook,
  Instagram,
  Check,
  ArrowRight,
  Search,
  Target,
  LayoutTemplate,
  Users,
  BarChart3,
  FlaskConical,
  MousePointerClick,
  EyeOff,
} from "lucide-react";
import { FOOTER_SOCIAL_LINKS } from "@/components/ui/SocialLinks";
import { HeroNav } from "@/components/ui/hero/HeroNav";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HeroSocialProof } from "@/components/ui/hero/HeroSocialProof";
import { Footer } from "@/components/ui/footer";
import { ContactSection } from "@/components/sections/ContactSection";
import { AboutSection } from "@/components/sections/AboutSection";
import { WhyUsSection } from "@/components/sections/WhyUsSection";
import { Users as UsersIcon, TrendingUp } from "lucide-react";
import type { GrowthItem } from "@/components/sections/WhyUsSection.data";
import { cn } from "@/lib/utils";
import logo from "@/assets/mind-media-logo.svg";

export const Route = createFileRoute("/paid-ads")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "Paid Ads — Google & Meta Management — Mind Media" },
      {
        name: "description",
        content:
          "Google and Meta ads run by a design-led team that also builds the landing page they point to. Clear cost-per-lead reporting, no long contracts.",
      },
      { property: "og:title", content: "Paid Ads — Google & Meta Management — Mind Media" },
      {
        property: "og:description",
        content:
          "Turn ad spend into customers, not wasted clicks. Ads + landing pages from one senior team.",
      },
    ],
  }),
  component: PaidAdsPage,
});

const gradText =
  "bg-gradient-to-r from-brand-blue via-purple-500 to-pink-500 bg-clip-text text-transparent";

const paidAdsGrowthItems: GrowthItem[] = [
  {
    title: "Reach ready-to-buy customers",
    description: "Target high-intent searches and audiences who will convert.",
    Icon: UsersIcon,
    bgClass: "bg-[#3b82f6]/10",
    textClass: "text-[#3b82f6]",
  },
  {
    title: "Turn clicks into leads",
    description: "Ads paired with landing pages built to convert, not for visits.",
    Icon: MousePointerClick,
    bgClass: "bg-[#a855f7]/10",
    textClass: "text-[#a855f7]",
  },
  {
    title: "Lower your cost per result",
    description: "Continuous testing and scale what actually pays back.",
    Icon: TrendingUp,
    bgClass: "bg-[#ec4899]/10",
    textClass: "text-[#ec4899]",
  },
];

function PaidAdsPage() {
  const navigate = useNavigate();
  const goToMockup = () => navigate({ to: "/get-mockup" });

  return (
    <main className="min-h-screen bg-background text-foreground">
      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-background pb-4 text-center">
        <div
          aria-hidden
          className="pointer-events-none absolute -left-40 -bottom-40 sm:-left-56 sm:-bottom-56 h-[420px] w-[420px] sm:h-[720px] sm:w-[720px] rounded-full bg-hero-glow-warm opacity-35 blur-3xl sm:blur-[120px]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-40 -top-40 sm:-right-56 sm:-top-56 h-[420px] w-[420px] sm:h-[720px] sm:w-[720px] rounded-full bg-hero-glow-cool opacity-40 blur-3xl sm:blur-[120px]"
        />
        <HeroNav
          logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
          navigation={[
            { label: "Work", href: "/#work" },
            { label: "About", href: "#about" },
            { label: "Services", href: "/#pricing" },
            { label: "Pricing", href: "#pricing" },
            { label: "FAQ", href: "#faq" },
            { label: "Contact", href: "#contact" },
          ]}
          ctaButton={{ label: "Book a Meeting", href: "#contact" }}
          mobilePricingHref="#pricing"
        />
        <div className="relative mx-auto px-6 mt-12 max-w-4xl -translate-y-5 text-left sm:text-center sm:mt-16 lg:max-w-none">
          <HeroSocialProof mobileAlign="left" />
          <h1 className="mx-auto max-w-3xl text-left text-4xl sm:text-center font-bold tracking-tight text-foreground sm:text-5xl lg:max-w-none lg:whitespace-nowrap lg:text-5xl">
            Turn ad spend into <span className={gradText}>customers,</span> not wasted clicks.
          </h1>
          <p className="mx-auto mt-5 max-w-5xl text-lg text-muted-foreground sm:text-xl">
            We run your Google and Meta ads and build the page they land on, so the whole funnel
            works, not just the click. Most agencies stop at the click. That's exactly where your
            money leaks.
          </p>
          <div className="mt-8 flex flex-wrap justify-start gap-3 sm:justify-center">
            <a
              href="#contact"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-[#ec4899] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-blue/40 transition hover:-translate-y-0.5 hover:opacity-90"
            >
              Get a Free Ad Audit <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#pricing"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-6 py-3 text-sm font-semibold text-foreground transition hover:-translate-y-0.5 hover:bg-accent"
            >
              Pricing
            </a>
          </div>
          <div className="relative mx-auto mt-8 flex flex-wrap justify-start gap-x-16 sm:justify-center gap-y-4">
            {[
              "Google & Meta Ads",
              "We build the landing page too",
              "Clear cost-per-lead reporting",
            ].map((p) => (
              <span key={p} className="inline-flex items-center gap-2 text-base font-medium">
                <span className="flex h-5 w-5 items-center justify-center rounded-full border border-brand-green bg-white">
                  <Check className="h-3 w-3 text-brand-green" strokeWidth={3} />
                </span>
                {p}
              </span>
            ))}
          </div>
        </div>
        <div className="relative">
          <WhyUsSection
            onCtaClick={goToMockup}
            variant="hero"
            showTrustBar={false}
            showRealExperience={false}
            withDivider={false}
            shareloId="CCLJjW5pChJStZBEFUSrg"
            shareloTitle="Paid ads — customer story"
            headline={
              <>
                <span className="block sm:inline">What great</span>{" "}
                <span className="block sm:inline">
                  <span className="inline-flex items-baseline whitespace-nowrap sm:inline">
                    paid ads
                    <span className="inline sm:hidden">{"\u00A0"}</span>
                    <span className="hidden sm:inline"> </span>
                    <span className="text-gradient-cycle align-baseline">actually do</span>
                  </span>
                </span>
              </>
            }
            growthItems={paidAdsGrowthItems}
          />
        </div>
      </section>

      {/* PROBLEM */}
      <section className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-5xl"
            titleClassName="lg:whitespace-nowrap"
            kickerWeight="font-bold"
            kicker="Why ad budgets disappear"
            title={
              <>
                Running ads is easy.{" "}
                <span className={gradText}>
                  Making them
                  <br className="sm:hidden" />
                  <span className="hidden sm:inline"> </span>pay is not.
                </span>
              </>
            }
          >
            Most businesses don't have a traffic problem. They have a leak problem. Here's where the
            money usually goes.
          </SectionHead>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                icon: MousePointerClick,
                n: "Clicks",
                h: "That never buy",
                p: "Sloppy targeting burns budget on people who were never going to become customers in the first place.",
              },
              {
                icon: LayoutTemplate,
                n: "The page",
                h: "That kills the sale",
                p: "A great ad sends people to a weak page and they bounce. The click cost you money and got you nothing.",
              },
              {
                icon: EyeOff,
                n: "No data",
                h: "So you fly blind",
                p: "Without proper tracking you can't tell what's working, so you keep paying for the campaigns that don't.",
              },
            ].map(({ icon: Icon, ...c }) => (
              <div key={c.h} className="rounded-2xl border border-border bg-card p-7 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <Icon className="h-5 w-5 text-brand-blue" strokeWidth={2.25} />
                  </div>
                  <div>
                    <div
                      className={cn(
                        "text-3xl tracking-tight md:text-4xl leading-none text-brand-blue font-bold",
                      )}
                    >
                      {c.n}
                    </div>
                    <h3 className="mt-0.5 text-lg font-bold tracking-tight leading-tight">{c.h}</h3>
                  </div>
                </div>
                <p className="mt-4 text-[15px] text-muted-foreground">{c.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INCLUDES */}
      <section id="includes" className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-4xl"
            kickerWeight="font-bold"
            kicker="What's included"
            title={
              <>
                The full funnel, <span className={gradText}>handled.</span>
              </>
            }
          >
            We don't just flip on ads and hope. We run the whole machine that turns spend into
            customers.
          </SectionHead>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: Search,
                h: "Google Search Ads",
                p: "Capture people the moment they search for what you sell, with high intent and a credit card ready.",
              },
              {
                icon: Instagram,
                h: "Meta Ads",
                p: "Facebook and Instagram campaigns that put you in front of the right audience before they're even searching.",
              },
              {
                icon: LayoutTemplate,
                h: "Landing pages that convert",
                p: "This is the difference. We design the page your ad lands on, so the click actually turns into a lead or sale.",
              },
              {
                icon: Target,
                h: "Audience & targeting",
                p: "We aim your budget at the people most likely to buy and cut the spend bleeding into everyone else.",
              },
              {
                icon: BarChart3,
                h: "Tracking & reporting",
                p: "Conversion tracking set up right, and a plain-English report showing your cost per lead and what it's returning.",
              },
              {
                icon: FlaskConical,
                h: "Testing & optimization",
                p: "We constantly test creative, copy, and audiences to lower your cost per result and scale the winners.",
              },
            ].map(({ icon: Icon, h, p }) => (
              <div
                key={h}
                className="group rounded-2xl border border-border bg-card p-7 transition hover:-translate-y-1 hover:border-brand-blue/30 hover:shadow-xl hover:shadow-brand-blue/10"
              >
                <div className="mb-4 flex items-center gap-3">
                  <div className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <Icon className="h-5 w-5 text-brand-blue" strokeWidth={2.25} />
                  </div>
                  <h3 className="text-lg font-bold tracking-tight">{h}</h3>
                </div>
                <p className="text-[15px] text-muted-foreground">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto max-w-5xl text-center">
            <Kicker className="font-bold">Why us</Kicker>
            <h2 className="mt-4 text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:whitespace-nowrap lg:text-5xl">
              Most agencies stop at the click. <span className={gradText}>We don't.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-4xl text-lg text-muted-foreground">
              Anyone can spend your budget and send you a traffic report. The click is only half the
              job. If the page that traffic lands on doesn't convert, you've paid for visitors, not
              customers. We're a design studio first, so we build the ad and the page as one
              machine.
            </p>
          </div>
          <div className="mt-12 grid items-center gap-12 lg:grid-cols-2">
            <ul className="space-y-5">
              {[
                [
                  "Ad and page, one team",
                  "No finger-pointing between the ad guy and the web guy. It's all us.",
                ],
                [
                  "You see the real numbers",
                  "Cost per lead and return, in plain English, not a dashboard you have to decode.",
                ],
                [
                  "No long contracts",
                  "You stay because it's making money, not because you're locked in.",
                ],
                [
                  "You own the accounts",
                  "The ad accounts and data are yours, set up under your name, always.",
                ],
              ].map(([title, body]) => (
                <li key={title} className="flex gap-4">
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <Check className="h-4 w-4 text-brand-blue" strokeWidth={3} />
                  </span>
                  <div>
                    <strong className="block text-base font-bold">{title}</strong>
                    <span className="text-[15px] text-muted-foreground">{body}</span>
                  </div>
                </li>
              ))}
            </ul>

            <div className="relative rounded-3xl bg-gradient-to-br from-brand-blue via-purple-600 to-pink-500 p-10 text-white shadow-2xl shadow-purple-500/30">
              <div className="absolute right-6 top-6 text-right">
                <div className="text-3xl font-bold leading-none">20+</div>
                <div className="text-xs font-medium uppercase tracking-wider text-white/80">
                  YEARS EXP
                </div>
              </div>
              <div className="text-6xl tracking-tight leading-none font-bold text-white">½</div>
              <div className="mt-2 text-lg font-bold">the job is the click</div>
              <p className="mt-5 text-[15px] leading-relaxed text-white/90">
                The other half is what happens after. A polished ad pointed at a weak page is the
                most common way money disappears in paid advertising. Owning both is how we turn the
                same budget into more customers instead of more bounces.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-4xl"
            kickerWeight="font-bold"
            kicker="How it works"
            title={
              <>
                From spend to <span className={gradText}>customers.</span>
              </>
            }
          />
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                n: 1,
                h: "Audit & strategy",
                p: "We look at your goals, your numbers, and your competitors, then pick the platform and offer with the fastest payoff.",
              },
              {
                n: 2,
                h: "Build ads & page",
                p: "We write the ads, create the visuals, and build the landing page they point to, all as one connected funnel.",
              },
              {
                n: 3,
                h: "Launch & track",
                p: "We go live with conversion tracking in place from day one, so every dollar is measured from the start.",
              },
              {
                n: 4,
                h: "Optimize & scale",
                p: "We cut what's not working, double down on what is, and push your cost per result down as it grows.",
              },
            ].map((s) => (
              <div
                key={s.n}
                className="flex items-center gap-5 rounded-2xl border border-border bg-card p-6"
              >
                <div
                  className={cn(
                    "flex-shrink-0 text-6xl leading-none tracking-tight font-bold text-brand-blue",
                  )}
                >
                  {s.n}
                </div>
                <div>
                  <h3 className="text-base font-bold tracking-tight">{s.h}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{s.p}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <AboutSection hideKicker hideMockupButton meetingButtonPrimary centerContent />

      {/* PRICING */}
      <section id="pricing" className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            kickerWeight="font-bold"
            kicker="Simple pricing"
            maxWidth="max-w-5xl"
            titleClassName="lg:whitespace-nowrap"
            title={
              <>
                Management plans that fit <span className={gradText}>your spend.</span>
              </>
            }
          >
            Flat monthly management, no surprise fees. Your ad budget is paid directly to Google and
            Meta and stays fully in your control.
          </SectionHead>
          <div className="grid items-stretch gap-6 md:grid-cols-3">
            {[
              {
                h: "Starter",
                desc: "For testing one channel",
                amt: "$1,200",
                sub: "Best for getting your first campaign live",
                b: [
                  "Google or Meta, one platform",
                  "One landing page included",
                  "Conversion tracking setup",
                  "Monthly report",
                ],
                cta: "ghost",
                feat: false,
              },
              {
                h: "Growth",
                desc: "For scaling what works",
                amt: "$2,800",
                sub: "Best for serious, steady lead flow",
                b: [
                  "Google and Meta, both",
                  "Multiple campaigns & landing pages",
                  "Ongoing creative testing",
                  "Bi-weekly reporting",
                ],
                cta: "primary",
                feat: true,
              },
              {
                h: "Scale",
                desc: "For higher budgets",
                amt: "Let's talk",
                sub: "Scoped to your spend and goals",
                b: [
                  "Everything in Growth",
                  "Aggressive multi-campaign testing",
                  "Retargeting & funnels",
                  "Dedicated strategy calls",
                ],
                cta: "ghost",
                feat: false,
              },
            ].map((p) => (
              <div
                key={p.h}
                className={cn(
                  "relative flex flex-col rounded-2xl border bg-card p-8",
                  p.feat
                    ? "border-transparent shadow-2xl shadow-purple-500/20 ring-2 ring-transparent [background:linear-gradient(var(--card),var(--card))_padding-box,linear-gradient(100deg,oklch(0.585_0.196_263),#a855f7,#ec4899)_border-box]"
                    : "border-border",
                )}
              >
                {p.feat && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-brand-blue via-purple-500 to-pink-500 px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-wider text-white">
                    Most popular
                  </span>
                )}
                <h3 className="text-lg font-bold tracking-tight">{p.h}</h3>
                <div className="mt-1 text-sm text-muted-foreground">{p.desc}</div>
                <div className="mt-5 text-4xl font-extrabold tracking-tight">
                  {p.amt}
                  {p.amt.startsWith("$") && (
                    <small className="ml-1 text-sm font-semibold text-muted-foreground">/mo</small>
                  )}
                </div>
                <div className="mt-1 text-xs font-medium text-muted-foreground">{p.sub}</div>
                <ul className="mt-6 flex-1 space-y-3">
                  {p.b.map((li) => (
                    <li key={li} className="flex items-start gap-2 text-sm font-medium">
                      <Check
                        className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-green"
                        strokeWidth={3}
                      />
                      {li}
                    </li>
                  ))}
                </ul>
                {p.amt === "Let's talk" ? (
                  <a
                    href="#contact"
                    className={cn(
                      "mt-6 inline-flex w-full items-center justify-center rounded-full px-5 py-3 text-sm font-bold transition hover:-translate-y-0.5",
                      p.cta === "primary"
                        ? "bg-gradient-to-r from-brand-blue to-[#ec4899] text-white shadow-lg shadow-brand-blue/40 hover:opacity-90"
                        : "border-2 border-brand-blue bg-background text-brand-blue hover:bg-brand-blue/10",
                    )}
                  >
                    Book a call
                  </a>
                ) : (
                  <button
                    onClick={goToMockup}
                    className={cn(
                      "mt-6 inline-flex w-full items-center justify-center rounded-full px-5 py-3 text-sm font-bold transition hover:-translate-y-0.5",
                      p.cta === "primary"
                        ? "bg-gradient-to-r from-brand-blue to-[#ec4899] text-white shadow-lg shadow-brand-blue/40 hover:opacity-90"
                        : "border-2 border-brand-blue bg-background text-brand-blue hover:bg-brand-blue/10",
                    )}
                  >
                    Get started
                  </button>
                )}
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-sm text-muted-foreground">
            Management fee only. Your ad budget is paid directly to Google and Meta, and stays fully
            in your control.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <SectionHead
            kickerWeight="font-bold"
            kicker="Questions"
            maxWidth="max-w-5xl"
            titleClassName="lg:whitespace-nowrap"
            title={
              <>
                What people ask <span className={gradText}>before starting.</span>
              </>
            }
          />
          {(() => {
            const items: [string, string][] = [
              [
                "How much should I spend on ads?",
                "It depends on your goals and margins, and we'll give you an honest starting number on the first call. Enough to get real data, not so much that a test feels like a gamble.",
              ],
              [
                "Do I pay you and the ad budget separately?",
                "Yes. You pay us a flat monthly management fee, and your ad budget goes straight to Google and Meta on your own accounts. We never mark it up or hold it.",
              ],
              [
                "How soon will I see results?",
                "Unlike SEO, ads work fast. You can see clicks and leads within days of launching. The first few weeks are about gathering data so we can sharpen targeting and cost.",
              ],
              [
                "Google or Meta, which is right for me?",
                "Google catches people already searching for what you sell. Meta puts you in front of the right audience before they search. We'll recommend where to start based on your business, often one first, then both.",
              ],
              [
                "Do you build the landing page too?",
                "Yes, and it's the whole point. We're a design studio first, so the page your ad lands on is built to convert. That's what separates spend that pays from spend that vanishes.",
              ],
              [
                "Is there a contract?",
                "No long lock-in. We ask for enough runway to gather data and prove it out, but you stay because it's working, not because you're trapped.",
              ],
            ];
            const col1 = items.filter((_, i) => i % 2 === 0);
            const col2 = items.filter((_, i) => i % 2 === 1);
            return (
              <div className="mx-auto mt-12 grid max-w-6xl gap-x-12 gap-y-2 md:grid-cols-2">
                <FaqColumn items={col1} />
                <FaqColumn items={col2} />
              </div>
            );
          })()}
        </div>
      </section>

      <ContactSection
        compact
        heading={
          <h2 className="text-4xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-5xl">
            See where your ads
            <br />
            are{" "}
            <span className="bg-gradient-to-r from-brand-blue to-[#ec4899] bg-clip-text text-transparent">
              leaking money.
            </span>
          </h2>
        }
        subheading={
          <p className="mt-6 text-lg text-muted-foreground">
            Get a free ad audit. We'll look at your account or your plan and show you exactly where
            the spend is going and how to fix it.
          </p>
        }
        ctaText="Get my free ad audit"
      />

      <Footer
        logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
        brandName="Mind Media"
        socialLinks={FOOTER_SOCIAL_LINKS}
        mainLinks={[
          { href: "/", label: "Home" },
          { href: "/seo", label: "SEO" },
          { href: "/paid-ads", label: "Paid Ads" },
          { href: "/#pricing", label: "Pricing" },
          { href: "#faq", label: "FAQ" },
          { href: "/blog", label: "Blog" },
          { href: "/services", label: "Services" },
        ]}
        legalLinks={[
          { href: "/privacy", label: "Privacy" },
          { href: "/terms", label: "Terms" },
        ]}
        copyright={{
          text: `© ${new Date().getFullYear()} Mind Media`,
          license: "All rights reserved",
        }}
      />
    </main>
  );
}

function Kicker({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-block rounded-full bg-brand-blue/10 px-4 py-1.5 text-xs uppercase tracking-[0.12em] text-brand-blue",
        className,
      )}
    >
      {children}
    </span>
  );
}

function SectionHead({
  kicker,
  title,
  children,
  kickerWeight = "font-bold",
  maxWidth = "max-w-2xl",
  titleClassName,
}: {
  kicker: string;
  title: React.ReactNode;
  children?: React.ReactNode;
  kickerWeight?: "font-bold" | "font-medium";
  maxWidth?: string;
  titleClassName?: string;
}) {
  return (
    <div className={cn("mx-auto mb-12 text-center", maxWidth)}>
      <Kicker className={kickerWeight}>{kicker}</Kicker>
      <h2
        className={cn(
          "mt-4 text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-5xl",
          titleClassName,
        )}
      >
        {title}
      </h2>
      {children && <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{children}</p>}
    </div>
  );
}

function FaqColumn({ items }: { items: [string, string][] }) {
  return (
    <Accordion type="single" collapsible className="w-full">
      {items.map(([q, a]) => (
        <AccordionItem key={q} value={q} className="border-b">
          <AccordionTrigger className="text-left text-base font-medium text-gray-950 md:text-lg">
            {q}
          </AccordionTrigger>
          <AccordionContent>
            <p className="text-muted-foreground text-base">{a}</p>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
